<template>
  <p class="body-2 mb-16 flex gap-6">
    <ActiveMarkets :weekend-delivery-configurations />
  </p>

  <ManageWeekendDeliveryDrawer>
    <template #body="{ close }">
      <p class="heading-2 mb-6">
        {{ i18n(translations.multipleMarketWeekendDeliveryDrawerHeadline) }}
      </p>
      <p class="mb-24">
        {{ i18n(translations.multipleMarketWeekendDeliveryDrawerInstruction) }}
      </p>
      <RevForm
        :id="formId"
        :initial-values="
          carrierWeekendDeliveryConfigurationsByCarrierByCountryByDay
        "
        :name="formId"
        noValidate
        @submit="updateWeekendDeliveryConfigurations($event, close)"
      >
        <template #default="{ values }">
          <RevAccordionList
            class="flex flex-col gap-24"
            :has-external-borders="false"
          >
            <RevAccordionItem
              v-for="carrierCodename of Object.keys(values)"
              :key="carrierCodename"
              :data-test="`accordion-toggle--${carrierCodename}`"
              :keep-content-alive="true"
              :wrapper-attrs="{
                class:
                  'bg-surface-default-low rounded-lg px-24 py-6 border-t-0 border-transparent',
              }"
              @click.prevent
            >
              <template #title>
                {{ findCarrierName(carrierCodename) }}
              </template>
              <template #description>
                <ActiveCarrierMarkets
                  :weekend-delivery-configurations="
                    weekendDeliveryConfigurations.filter(
                      (configuration) =>
                        carrierCodename === configuration.carrierCodename,
                    )
                  "
                />
              </template>
              <template #body>
                <table
                  class="text-body-1 w-full px-24 py-8 text-left"
                  :data-test="`accordion-content--${carrierCodename}`"
                >
                  <thead>
                    <tr class="border-static-default-low border-b-1">
                      <th class="p-16">
                        {{
                          i18n(
                            translations.multipleMarketWeekendDeliveryConfigurationFormTableHeadline,
                          )
                        }}
                      </th>
                      <th class="p-16">
                        <RevCheckbox
                          :id="`select-all--${carrierCodename}--SATURDAY`"
                          :checked="
                            isAllCarrierMarketsSelected(
                              values,
                              carrierCodename,
                              'SATURDAY',
                            )
                          "
                          :data-test="`select-all--${carrierCodename}--SATURDAY`"
                          @update:model-value="
                            (newValue) =>
                              selectAllCarrier(
                                values,
                                carrierCodename,
                                'SATURDAY',
                                newValue,
                              )
                          "
                        >
                          <template #label>
                            {{
                              i18n(
                                translations.multipleMarketWeekendDeliveryConfigurationFormTableHeadSelectAll,
                              )
                            }}
                          </template>
                        </RevCheckbox>
                      </th>
                      <th class="p-16">
                        <RevCheckbox
                          :id="`select-all--${carrierCodename}--SUNDAY`"
                          :checked="
                            isAllCarrierMarketsSelected(
                              values,
                              carrierCodename,
                              'SUNDAY',
                            )
                          "
                          :data-test="`select-all--${carrierCodename}--SUNDAY`"
                          @update:model-value="
                            (newValue) =>
                              selectAllCarrier(
                                values,
                                carrierCodename,
                                'SUNDAY',
                                newValue,
                              )
                          "
                        >
                          <template #label>
                            {{
                              i18n(
                                translations.multipleMarketWeekendDeliveryConfigurationFormTableHeadSelectAll,
                              )
                            }}
                          </template>
                        </RevCheckbox>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="countryCode of Object.keys(
                        values[carrierCodename],
                      )"
                      :key="countryCode"
                      class="border-static-default-low border-b-1"
                    >
                      <td class="flex items-center gap-8 p-16">
                        <Flag :country-code="countryCode as Country" />
                        <span class="body-1 h-24">
                          {{ i18n.country(countryCode) }}
                        </span>
                      </td>
                      <td class="p-16">
                        <RevCheckbox
                          :id="`select-carrier--${carrierCodename}--${countryCode}--SATURDAY`"
                          v-model="
                            values[carrierCodename][countryCode as Country]
                              .SATURDAY.workingDay
                          "
                          @update:model-value="watchChanges(values)"
                        >
                          <template #label>
                            {{ getWeekday('SATURDAY') }}
                          </template>
                        </RevCheckbox>
                      </td>
                      <td class="p-16">
                        <RevCheckbox
                          :id="`select-carrier--${carrierCodename}--${countryCode}--SUNDAY`"
                          v-model="
                            values[carrierCodename][countryCode as Country]
                              .SUNDAY.workingDay
                          "
                          @update:model-value="watchChanges(values)"
                        >
                          <template #label>
                            {{ getWeekday('SUNDAY') }}
                          </template>
                        </RevCheckbox>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </template>
            </RevAccordionItem>
          </RevAccordionList>
        </template>
      </RevForm>
    </template>
    <template #footer>
      <div class="flex justify-end">
        <RevButton
          :disabled="!isConfigurationUpdated || isSubmittingForm"
          :form="formId"
          :loading="isSubmittingForm"
          type="submit"
          variant="primary"
        >
          {{
            i18n(
              translations.multipleMarketWeekendDeliveryConfigurationFormSubmitButton,
            )
          }}
        </RevButton>
      </div>
    </template>
    <template #trigger="{ open }">
      <RevButton
        :icon="IconChevronRight"
        size="small"
        variant="secondary"
        @click="open"
      >
        {{
          i18n(
            isWeekendDeliveryConfigured
              ? translations.multipleMarketWeekendDeliveryTriggerUpdate
              : translations.multipleMarketWeekendDeliveryTriggerSetUp,
          )
        }}
      </RevButton>
    </template>
  </ManageWeekendDeliveryDrawer>
</template>

<script setup lang="ts">
import { computed, ref, useId } from 'vue'

import { Country } from '@backmarket/http-api'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { groupBy } from '@backmarket/utils/object/groupBy'
import { isEqual } from '@backmarket/utils/object/isEqual'
import { RevAccordionItem } from '@ds/components/AccordionItem'
import { RevAccordionList } from '@ds/components/AccordionList'
import { RevButton } from '@ds/components/Button'
import { RevCheckbox } from '@ds/components/Checkbox'
import { RevForm } from '@ds/components/Form'
import { setFormValues } from '@ds/components/Form/Form.actions'
import { IconChevronRight } from '@ds/icons/IconChevronRight'

import type {
  WeekendDay,
  WeekendDeliveryConfiguration,
  WeekendDeliveryConfigurationsGroupedBy,
} from '../../ManageWeekendDelivery.types'
import { useGetWeekday } from '../../composables/useGetWeekday'
import { useUpdateCarrierWeekendDeliveriesConfiguration } from '../../composables/useUpdateCarrierWeekendDeliveriesConfiguration'
import ManageWeekendDeliveryDrawer from '../ManageWeekendDeliveryDrawer/ManageWeekendDeliveryDrawer.vue'

import translations from './MultipleMarketWeekendDelivery.translations'
import type {
  MultipleMarketWeekendDeliveryProps,
  WeekendDeliveryConfigurationByCarrierCodenameByCountryByWeekendDay,
} from './MultipleMarketWeekendDelivery.types'
import ActiveCarrierMarkets from './components/ActiveCarrierMarkets/ActiveCarrierMarkets.vue'
import ActiveMarkets from './components/ActiveMarkets/ActiveMarkets.vue'

const i18n = useI18n()
const formId = useId()
const getWeekday = useGetWeekday()
const updateCarrierWeekendDeliveriesConfiguration =
  useUpdateCarrierWeekendDeliveriesConfiguration()

const { isWeekendDeliveryConfigured, weekendDeliveryConfigurations } =
  defineProps<MultipleMarketWeekendDeliveryProps>()
const emit = defineEmits(['updated'])

const isConfigurationUpdated = ref(false)
const isSubmittingForm = ref(false)

const carrierWeekendDeliveryConfigurationsByCarrierByCountryByDay =
  computed<WeekendDeliveryConfigurationByCarrierCodenameByCountryByWeekendDay>(
    () => {
      const carrierWeekendDeliveryConfigurationsByCarrier = groupBy(
        weekendDeliveryConfigurations,
        ({ carrierCodename }) => carrierCodename,
      ) as WeekendDeliveryConfigurationsGroupedBy<'carrierCodename'>

      return Object.entries(
        carrierWeekendDeliveryConfigurationsByCarrier,
      ).reduce((acc, [carrierCodename, configurations]) => {
        const configurationsByCountry = groupBy(
          configurations,
          ({ countryCode }) => countryCode,
        )

        return {
          ...acc,
          [carrierCodename]: Object.entries(configurationsByCountry).reduce(
            (subAcc, [countryCode, subConfigurations]) => {
              return {
                ...subAcc,
                [countryCode]: {
                  SATURDAY: subConfigurations.find(
                    (configuration) => configuration.day === 'SATURDAY',
                  ),
                  SUNDAY: subConfigurations.find(
                    (configuration) => configuration.day === 'SUNDAY',
                  ),
                },
              }
            },
            {},
          ),
        }
      }, {})
    },
  )

function findCarrierName(
  carrierCodename: WeekendDeliveryConfiguration['carrierCodename'],
) {
  const weekendDeliveryConfiguration = weekendDeliveryConfigurations.find(
    (configuration) => configuration.carrierCodename === carrierCodename,
  )

  return weekendDeliveryConfiguration?.carrierName
}

function isAllCarrierMarketsSelected(
  formValues: WeekendDeliveryConfigurationByCarrierCodenameByCountryByWeekendDay,
  carrierCodename: keyof WeekendDeliveryConfigurationByCarrierCodenameByCountryByWeekendDay,
  weekendDay: WeekendDay,
) {
  return Object.values(formValues[carrierCodename]).every(
    (configurationByDay) => configurationByDay[weekendDay].workingDay,
  )
}

function selectAllCarrier(
  formValues: WeekendDeliveryConfigurationByCarrierCodenameByCountryByWeekendDay,
  carrierCodename: WeekendDeliveryConfiguration['carrierCodename'],
  weekendDay: WeekendDay,
  isSelectingAllCarrier: boolean | Array<string>,
) {
  const newValues = {
    ...formValues,
    [carrierCodename]: Object.entries(formValues[carrierCodename]).reduce(
      (acc, [countryCode, configurationByDay]) => {
        return {
          ...acc,
          [countryCode]: {
            ...configurationByDay,
            [weekendDay]: {
              ...configurationByDay[weekendDay],
              workingDay: !!isSelectingAllCarrier,
            },
          },
        }
      },
      {},
    ),
  }
  setFormValues(formId, newValues)
  watchChanges(
    newValues as WeekendDeliveryConfigurationByCarrierCodenameByCountryByWeekendDay,
  )
}

function watchChanges(
  formValues: WeekendDeliveryConfigurationByCarrierCodenameByCountryByWeekendDay,
) {
  isConfigurationUpdated.value = !isEqual(
    formValues,
    carrierWeekendDeliveryConfigurationsByCarrierByCountryByDay.value,
  )
}

async function updateWeekendDeliveryConfigurations(
  formValues: WeekendDeliveryConfigurationByCarrierCodenameByCountryByWeekendDay,
  onSubmit: () => void,
) {
  isSubmittingForm.value = true
  const flattenedUpdatedWeekendDeliveryConfigurations = Object.values(
    formValues,
  )
    .flatMap((configurationByCountryCode) =>
      Object.values(configurationByCountryCode),
    )
    .flatMap((configurationByWeekendDay) =>
      Object.values(configurationByWeekendDay),
    )

  try {
    await updateCarrierWeekendDeliveriesConfiguration(
      weekendDeliveryConfigurations,
      flattenedUpdatedWeekendDeliveryConfigurations,
    )
    emit('updated')
    onSubmit()
  } catch (error) {
    isSubmittingForm.value = false
  }
}
</script>
